import React from "react"
import { DebouncedInput } from "./@components/DebouncedInput"

export const resolveParameters = ({ primitive, activeParameters, showParents, metadataFallback }) => {
  if (showParents) {
    if (primitive.origin && primitive.origin.childParameters) {
      return primitive.origin.childParameters
    }
    if (primitive.task && primitive.task.itemParameters?.[primitive.referenceId]) {
      return primitive.task.itemParameters[primitive.referenceId]
    }
    return undefined
  }
  return activeParameters ?? (primitive.metadata?.parameters || metadataFallback?.[primitive.type]?.parameters)
}

export const buildFieldList = ({
  primitive,
  parameters,
  source,
  fieldsOverride,
  hidden,
  includeAutoParam,
  fullList,
  showExtra,
}) => {
  if (!parameters) {
    return { items: [], fields: [] }
  }

  let parameterMap = parameters
  let fields = Object.keys(parameterMap).sort(
    (a, b) =>
      (parameterMap[a].order === undefined ? 99 : parameterMap[a].order) -
      (parameterMap[b].order === undefined ? 99 : parameterMap[b].order)
  )

  if (fieldsOverride) {
    const remap = fieldsOverride.reduce((acc, field) => {
      if (field instanceof Object) {
        Object.keys(field).forEach((key) => {
          parameterMap = parameterMap === parameters ? { ...parameters } : parameterMap
          parameterMap[key] = { ...(parameterMap[key] ?? {}), type: field[key] }
          acc[key] = key
        })
      } else {
        acc[field] = field
      }
      return acc
    }, {})
    const keyNames = Object.keys(remap)
    fields = fields.filter((field) => keyNames.includes(field))
  }

  if (includeAutoParam) {
    fields = fields.concat(Object.keys(source ?? {})).filter((d, i, a) => a.indexOf(d) === i)
  }

  fields = fields.filter((field) => !parameterMap[field]?.hidden)
  if (hidden) {
    const hide = [hidden].flat()
    fields = fields.filter((field) => !hide.includes(field))
  }

  let items = fields.map((key) => {
    const config = parameterMap[key]
    if (!config) {
      const value = source?.[key]
      if (Array.isArray(value)) {
        return { type: "string", title: `${key} (auto)`, value: `${value.length} items\n${JSON.stringify(value)}`, key }
      }
      if (typeof value === "object") {
        return { type: "string", title: `${key} (auto)`, value, key }
      }
      return { type: "string", title: `${key} (auto)`, value, key }
    }

    let val
    const parts = key.split(".")
    if (parts.length === 1) {
      val = source?.[key]
    } else {
      let node = source
      const last = parts.pop()
      for (const part of parts) {
        if (node) {
          node = node[part]
        }
      }
      val = node?.[last]
    }
    if (val === undefined && config.default !== undefined) {
      val = config.default
    }
    return { ...config, title: config.title ?? config.label ?? key, value: val, autoId: source?.[`${key}Id`], key }
  })

  items = items.filter((item) => {
    if (item.condition) {
      const pass = Object.keys(item.condition).reduce((acc, key) => {
        let expected = item.condition[key]
        let invert = false
        if (expected[0] === "!") {
          expected = expected.slice(1)
          invert = true
        }
        const matches = primitive.referenceParameters?.[key] === expected
        return acc && (matches ^ invert)
      }, true)
      return pass
    }
    return true
  })

  if (fullList) {
    if (!showExtra) {
      items = items.filter((item) => item.value || !item.extra)
    }
  } else {
    items = items.filter((item) => item.value !== undefined || item.default || item.type === "primitive_parent")
  }

  return { items, fields }
}

export const FieldList = ({
  items,
  primitive,
  itemsSource,
  editing,
  editable,
  mode = "form",
  density = "md",
  showLabel = true,
  includeTitle,
  leftAlign,
  className,
  potentialTarget,
  renderItem,
}) => {
  if (!items || items.length === 0) {
    return null
  }

  return (
    <div
      style={{
        gridTemplateColumns: showLabel ? "minmax(min-content, 7rem) 1fr" : "1fr",
      }}
      className={`grid gap-2 text-sm ${className ?? ""}`}
    >
      {includeTitle && (
        <>
          {showLabel && <p className="pl-1 py-1 mr-2 shrink-0 grow-0">Title</p>}
          <DebouncedInput
            size={density === "sm" ? "sm" : "md"}
            variant="bordered"
            value={primitive.title}
            placeholder={primitive.metadata?.placeholder ?? "Empty"}
            className="w-full"
            classNames={{
              input: "text-slate-700",
            }}
            onChange={(value) => { primitive.title = value }}
          />
        </>
      )}
      {items.map((item, idx) => (
        <React.Fragment key={item.key ?? idx}>
          {showLabel && <p className="pl-1 py-1 mr-2 shrink-0 grow-0 text-slate-500 dark:text-slate-300">{item.title ?? item.label ?? item.key}</p>}
          {potentialTarget && potentialTarget.includes(`referenceParameters.${item.key}`)
            ? <div className="w-full p-3.5 bg-gray-100 rounded animate-pulse" />
            : renderItem({
                item,
                editing: editable ?? editing,
                editable: editable ?? editing,
                primitive,
                items: itemsSource,
                density,
                mode,
                showLabel,
                leftAlign,
              })
          }
        </React.Fragment>
      ))}
    </div>
  )
}

export const buildPinItems = ({ primitive, pins, dataForNewInstance }) => {
  const pinsToRender = Object.keys(pins).filter((key) =>
    key.split || pins[key].types?.includes("string") || pins[key].types?.includes("string_list") || pins[key].types?.includes("url") || pins[key].format === "url"
  )

  return pinsToRender.map((pinName) => {
    const pinConfig = pins[pinName]
    if (pinConfig.split) {
      return { pinName, pinConfig, split: true }
    }

    const options = {}
    let pinType = pinConfig.format ?? (pinConfig.types?.includes("url") ? "url" : "long_string")
    if (pinConfig.name?.toLowerCase().includes("urls")) {
      options.urlLimit = 50
      pinType = "verified_url"
    } else if (pinConfig.name?.toLowerCase().includes("url")) {
      pinType = "verified_url"
    }

    return {
      pinName,
      pinConfig,
      split: false,
      item: {
        type: pinType,
        ...options,
        value: dataForNewInstance ? dataForNewInstance[pinName] : primitive.referenceParameters?.[pinName],
        key: pinName,
        placeholder: pinConfig.placeholder ?? pinConfig.example,
      },
    }
  })
}
